import React, { useState, useEffect, useReducer } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    Button,
    TextField,
    InputAdornment,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    Typography,
    Box
} from "@material-ui/core";
import { Search, Refresh, GetApp, Delete as DeleteIcon } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import * as campaignLeadsService from "../../services/campaignLeads";
import * as campaignLandingPagesService from "../../services/campaignLandingPages";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import LeadDetailsModal from "../../components/LeadDetailsModal";

const useStyles = makeStyles((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(2),
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
    chip: {
        margin: theme.spacing(0.5),
    },
    clickableRow: {
        cursor: "pointer",
        transition: "background-color 0.2s",
        "&:hover": {
            backgroundColor: theme.palette.action.hover
        }
    }
}));

const reducer = (state, action) => {
    if (action.type === "LOAD_LEADS") {
        const leads = action.payload;
        const newLeads = [];

        leads.forEach((lead) => {
            const leadIndex = state.findIndex((l) => l.id === lead.id);
            if (leadIndex !== -1) {
                state[leadIndex] = lead;
            } else {
                newLeads.push(lead);
            }
        });

        return [...state, ...newLeads];
    }

    if (action.type === "DELETE_LEAD") {
        return state.filter((lead) => lead.id !== action.payload);
    }

    if (action.type === "RESET") {
        return [];
    }
};

const CampaignLeads = () => {
    const classes = useStyles();

    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [searchParam, setSearchParam] = useState("");
    const [landingPageId, setLandingPageId] = useState("");
    const [landingPages, setLandingPages] = useState([]);
    const [leads, dispatch] = useReducer(reducer, []);
    const [selectedLead, setSelectedLead] = useState(null);
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [deletingLead, setDeletingLead] = useState(null);
    // New filter states
    const [utmSources, setUtmSources] = useState([]);
    const [availableUtmSources, setAvailableUtmSources] = useState([]);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [formType, setFormType] = useState("");

    useEffect(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
    }, [searchParam, landingPageId, utmSources, startDate, endDate, formType]);

    // Load available UTM sources
    useEffect(() => {
        const fetchUtmSources = async () => {
            try {
                const sources = await campaignLeadsService.getUtmSources();
                setAvailableUtmSources(sources);
            } catch (err) {
                console.error("Error loading UTM sources:", err);
            }
        };
        fetchUtmSources();
    }, []);

    useEffect(() => {
        setLoading(true);
        const delayDebounceFn = setTimeout(() => {
            const fetchLeads = async () => {
                try {
                    const { leads: data, hasMore: more } = await campaignLeadsService.getAll({
                        searchParam,
                        pageNumber,
                        landingPageId,
                        utmSources: utmSources.join(','),
                        startDate,
                        endDate,
                        formType
                    });
                    dispatch({ type: "LOAD_LEADS", payload: data });
                    setHasMore(more);
                    setLoading(false);
                } catch (err) {
                    toast.error("Erro ao carregar leads");
                    setLoading(false);
                }
            };
            fetchLeads();
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchParam, pageNumber, landingPageId]);

    useEffect(() => {
        const fetchLandingPages = async () => {
            try {
                const data = await campaignLandingPagesService.getAll();
                setLandingPages(data);
            } catch (err) {
                toast.error("Erro ao carregar landing pages");
            }
        };
        fetchLandingPages();
    }, []);

    const handleSearch = (event) => {
        setSearchParam(event.target.value.toLowerCase());
    };

    const handleLoadMore = () => {
        setPageNumber((prevState) => prevState + 1);
    };

    const getSourceColor = (source) => {
        const colors = {
            google: "primary",
            facebook: "secondary",
            direto: "default",
            outros: "default"
        };
        return colors[source] || "default";
    };

    const getSourceLabel = (source) => {
        const labels = {
            google: "Google",
            facebook: "Facebook",
            direto: "Direto",
            outros: "Outros"
        };
        return labels[source] || source;
    };

    const handleExportExcel = async () => {
        try {
            // Buscar todos os leads com os filtros atuais
            const { leads: allLeads } = await campaignLeadsService.getAll({
                searchParam,
                landingPageId,
                pageNumber: 1
            });

            // Criar CSV
            const headers = ["Nome", "Telefone", "Email", "Origem", "Campanha", "Landing Page", "Data"];
            const rows = allLeads.map(lead => [
                lead.name || "-",
                lead.phone,
                lead.email || "-",
                getSourceLabel(lead.sourceType),
                lead.utmCampaign || "-",
                lead.landingPage?.name || "-",
                format(parseISO(lead.createdAt), "dd/MM/yyyy HH:mm")
            ]);

            // Converter para CSV
            const csvContent = [
                headers.join(","),
                ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
            ].join("\n");

            // Download
            const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `leads_${new Date().toISOString().split('T')[0]}.csv`;
            link.click();

            toast.success("Leads exportados com sucesso!");
        } catch (err) {
            toast.error("Erro ao exportar leads");
        }
    };

    const handleRowClick = (lead) => {
        setSelectedLead(lead);
        setDetailsModalOpen(true);
    };

    const handleCloseDetailsModal = () => {
        setDetailsModalOpen(false);
        setSelectedLead(null);
    };

    const handleDeleteLead = async (leadId, event) => {
        event.stopPropagation(); // Prevent row click

        if (!window.confirm("Tem certeza que deseja excluir este lead?")) {
            return;
        }

        setDeletingLead(leadId);
        try {
            await campaignLeadsService.deleteLead(leadId);
            toast.success("Lead excluído com sucesso");
            // Remove lead from state
            dispatch({ type: "DELETE_LEAD", payload: leadId });
        } catch (err) {
            toast.error("Erro ao excluir lead");
        } finally {
            setDeletingLead(null);
        }
    };

    return (
        <MainContainer>
            <MainHeader>
                <Title>Leads de Campanha</Title>
            </MainHeader>

            {/* Filtros em linha separada */}
            <Box p={2} display="flex" flexWrap="wrap" gap={1} alignItems="center" style={{ backgroundColor: '#f5f5f5' }}>
                <TextField
                    placeholder="Buscar..."
                    type="search"
                    value={searchParam}
                    onChange={handleSearch}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search style={{ color: "gray" }} />
                            </InputAdornment>
                        ),
                    }}
                    variant="outlined"
                    size="small"
                    style={{ minWidth: 200, backgroundColor: 'white' }}
                />

                {/* Filtro de Origens UTM (Múltiplo) */}
                <FormControl variant="outlined" size="small" style={{ minWidth: 200, backgroundColor: 'white' }}>
                    <InputLabel>Origens</InputLabel>
                    <Select
                        multiple
                        value={utmSources}
                        onChange={(e) => setUtmSources(e.target.value)}
                        renderValue={(selected) => selected.length > 0 ? selected.join(', ') : ''}
                        label="Origens"
                    >
                        {availableUtmSources.map((source) => (
                            <MenuItem key={source} value={source}>
                                <Checkbox checked={utmSources.indexOf(source) > -1} />
                                <ListItemText primary={source} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl variant="outlined" size="small" style={{ minWidth: 200, backgroundColor: 'white' }}>
                    <InputLabel>Landing Page</InputLabel>
                    <Select
                        value={landingPageId}
                        onChange={(e) => setLandingPageId(e.target.value)}
                        label="Landing Page"
                    >
                        <MenuItem value="">Todas</MenuItem>
                        {landingPages.map((lp) => (
                            <MenuItem key={lp.id} value={lp.id}>{lp.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    label="Data Início"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    size="small"
                    style={{ width: 160, backgroundColor: 'white' }}
                />

                <TextField
                    label="Data Fim"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    size="small"
                    style={{ width: 160, backgroundColor: 'white' }}
                />

                <FormControl variant="outlined" size="small" style={{ minWidth: 120, backgroundColor: 'white' }}>
                    <InputLabel>Tipo</InputLabel>
                    <Select
                        value={formType}
                        onChange={(e) => setFormType(e.target.value)}
                        label="Tipo"
                    >
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="quiz">Quiz</MenuItem>
                        <MenuItem value="pwa">PWA</MenuItem>
                    </Select>
                </FormControl>

                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                        setUtmSources([]);
                        setStartDate("");
                        setEndDate("");
                        setFormType("");
                        setLandingPageId("");
                        setSearchParam("");
                    }}
                >
                    Limpar
                </Button>

                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<GetApp />}
                    onClick={handleExportExcel}
                >
                    Exportar
                </Button>

                <IconButton size="small" onClick={() => window.location.reload()}>
                    <Refresh />
                </IconButton>
            </Box>
            <Paper className={classes.mainPaper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Nome</TableCell>
                            <TableCell>Telefone</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Origem</TableCell>
                            <TableCell>Dispositivo</TableCell>
                            <TableCell>Localização</TableCell>
                            <TableCell>Data</TableCell>
                            <TableCell align="center">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <>
                            {leads.map((lead) => (
                                <TableRow
                                    key={lead.id}
                                    className={classes.clickableRow}
                                    onClick={() => handleRowClick(lead)}
                                >
                                    <TableCell>{lead.name || "-"}</TableCell>
                                    <TableCell>{lead.phone}</TableCell>
                                    <TableCell>{lead.email || "-"}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={lead.utm_source || lead.sourceType || "Direto"}
                                            color={getSourceColor(lead.utm_source || lead.sourceType)}
                                            size="small"
                                            className={classes.chip}
                                        />
                                        {lead.utm_campaign && (
                                            <Typography variant="caption" display="block" color="textSecondary">
                                                {lead.utm_campaign}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {lead.device_type && (
                                            <Typography variant="body2">
                                                {lead.device_type === "mobile" ? "📱" : lead.device_type === "tablet" ? "📱" : "💻"} {lead.device_type}
                                            </Typography>
                                        )}
                                        {lead.os_name && (
                                            <Typography variant="caption" color="textSecondary">
                                                {lead.os_name}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {lead.city && (
                                            <Typography variant="body2">
                                                📍 {lead.city}, {lead.region}
                                            </Typography>
                                        )}
                                        {lead.country && (
                                            <Typography variant="caption" color="textSecondary">
                                                {lead.country}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>{format(parseISO(lead.createdAt), "dd/MM/yyyy HH:mm")}</TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => handleDeleteLead(lead.id, e)}
                                            disabled={deletingLead === lead.id}
                                            color="secondary"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {loading && <TableRowSkeleton columns={7} />}
                        </>
                    </TableBody>
                </Table>
                {hasMore && (
                    <Box display="flex" justifyContent="center" mt={2}>
                        <Button variant="outlined" onClick={handleLoadMore}>
                            Carregar Mais
                        </Button>
                    </Box>
                )}
            </Paper>

            <LeadDetailsModal
                open={detailsModalOpen}
                onClose={handleCloseDetailsModal}
                lead={selectedLead}
            />
        </MainContainer>
    );
};

export default CampaignLeads;
``